import {
    Box,
    IconButton,
    Icon,
    Typography,
    Grid,
    FormRadioButtonGroup,
    FormInputText,
    RegexMapOptions,
    FormRangeBlocks,
    FormIconButton,
    useTheme,
    Tooltip,
    PargodaInfoIcon,
} from '../../../../../../base';
import {
    BaseJobRequirementFields,
    ISaveJobRequirement,
    ISaveJobForm,
    SAVE_JOB_FORM_VALUES,
    RequirementLevels,
    TooltipPlacement,
} from '../../../../../../common';
import { useFormContext } from 'react-hook-form';
import {
    StyledGrid,
    ImportanceIndicator,
    SkillRange,
} from './RequirementItem.styles';
import { useTranslation } from 'react-i18next';

interface Props {
    requirement: ISaveJobRequirement;
    requirementIndex: number;
    isFirstItem: boolean;
    onUpdateRequirement: () => void;
    onDelete: () => void;
}

const RequirementItem = ({
    requirement,
    requirementIndex,
    isFirstItem,
    onDelete,
    onUpdateRequirement,
}: Props) => {
    const { control } = useFormContext<ISaveJobForm>();
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <div style={{ margin: '0 24px' }}>
            <StyledGrid
                container
                alignItems="stretch"
                flexWrap="nowrap"
                isFirstItem={isFirstItem}
            >
                <Grid
                    item
                    xs={3}
                    container
                    direction="column"
                    alignItems="flex-start"
                    sx={{ height: 'auto' }}
                >
                    {requirement.isAIRecommendation && (
                        <Icon
                            name="AIStarsIcon"
                            size={20}
                            color={theme.palette.secondary.main}
                        />
                    )}
                    <Box
                        display="flex"
                        alignItems="center"
                        flexGrow={1}
                        width="100%"
                    >
                        <Tooltip title={requirement.name} placement="top">
                            <Typography variant="body1">
                                {requirement.name}
                            </Typography>
                        </Tooltip>
                    </Box>
                </Grid>

                <Grid item flexGrow={1}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} lg={4}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Box maxWidth={200}>
                                        <Box
                                            sx={{ mb: 0.5 }}
                                            display="flex-start"
                                            flexWrap="wrap"
                                        >
                                            <Box display="flex">
                                                <Typography variant="body2">
                                                    {t('saveJob.importance')}
                                                </Typography>
                                                <PargodaInfoIcon
                                                    text={t(
                                                        'saveJob.importanceInfo'
                                                    )}
                                                    placement={
                                                        TooltipPlacement.TOP_START
                                                    }
                                                    size={16}
                                                    iconSx={{
                                                        marginInlineStart:
                                                            theme.spacing(0.5),
                                                    }}
                                                    wrapperSx={{
                                                        alignItems: 'center',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box
                                            display="flex"
                                            justifyContent="top"
                                        >
                                            <ImportanceIndicator>
                                                <FormRangeBlocks
                                                    name={`${SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS}.${requirementIndex}.${BaseJobRequirementFields.WEIGHT}`}
                                                    selectMaxOnly={true}
                                                />
                                            </ImportanceIndicator>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} lg={8}>
                            <Grid container spacing={1}>
                                <Grid item xs={5.5}>
                                    <Box
                                        sx={{ mb: 0.5 }}
                                        display="flex"
                                        flexWrap="wrap"
                                    >
                                        <Typography variant="body2">
                                            {t('saveJob.typeOfRequirement')}
                                        </Typography>
                                        <PargodaInfoIcon
                                            text={t(
                                                'saveJob.typeOfRequirementInfo'
                                            )}
                                            placement={
                                                TooltipPlacement.TOP_START
                                            }
                                            size={16}
                                            iconSx={{
                                                marginInlineStart:
                                                    theme.spacing(0.5),
                                            }}
                                            wrapperSx={{
                                                alignItems: 'center',
                                            }}
                                        />
                                    </Box>
                                    <FormRadioButtonGroup
                                        name={`${SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS}.${requirementIndex}.${BaseJobRequirementFields.IMPORTANCE_LEVEL}`}
                                        control={control}
                                        direction="column"
                                        size="small"
                                        buttonsContent={[
                                            {
                                                key: RequirementLevels.regular,
                                                label: t('saveJob.regular'),
                                            },
                                            {
                                                key: RequirementLevels.mandatory,
                                                label: t('saveJob.mandatory'),
                                            },
                                            {
                                                key: RequirementLevels.advantage,
                                                label: t('saveJob.advantage'),
                                            },
                                        ]}
                                    />
                                </Grid>
                                <Grid item xs={6.5}>
                                    <Box>
                                        <Box
                                            mb={0.5}
                                            color="inherit"
                                            display="flex"
                                            flexWrap="wrap"
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ mr: 0.5 }}
                                            >
                                                {t('saveJob.yearsOfExperience')}
                                            </Typography>
                                            <PargodaInfoIcon
                                                text={t(
                                                    'saveJob.yearsOfExperienceInfo'
                                                )}
                                                placement={
                                                    TooltipPlacement.TOP_START
                                                }
                                                size={16}
                                                iconSx={{
                                                    marginInlineStart:
                                                        theme.spacing(0.5),
                                                }}
                                                wrapperSx={{
                                                    alignItems: 'center',
                                                }}
                                            />
                                        </Box>
                                        <SkillRange>
                                            <FormInputText
                                                name={`${SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS}.${requirementIndex}.${BaseJobRequirementFields.RANGE_FROM}`}
                                                control={control}
                                                sx={{ width: 60 }}
                                                validateExpression={
                                                    RegexMapOptions.numberOnly
                                                }
                                                isTextAlignCenter
                                                inputProps={{ maxLength: 2 }}
                                                hideCharacterCount
                                                removeBottomPadding
                                            />
                                            <Box
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                —
                                            </Box>
                                            <FormInputText
                                                name={`${SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS}.${requirementIndex}.${BaseJobRequirementFields.RANGE_TO}`}
                                                control={control}
                                                sx={{ width: 60 }}
                                                validateExpression={
                                                    RegexMapOptions.numberOnly
                                                }
                                                isTextAlignCenter
                                                inputProps={{ maxLength: 2 }}
                                                hideCharacterCount
                                                removeBottomPadding
                                            />
                                        </SkillRange>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid
                    item
                    width="100px"
                    display="flex"
                    justifyContent="flex-end"
                    alignSelf="flex-start"
                >
                    <Tooltip
                        title={t('saveJob.invisibleRequirementInfo')}
                        placement={TooltipPlacement.TOP_START}
                    >
                        <FormIconButton
                            name={`${SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS}.${requirementIndex}.${BaseJobRequirementFields.IS_VISIBLE}`}
                            control={control}
                            onClick={() => {
                                onUpdateRequirement();
                            }}
                            size="small"
                        >
                            <Icon
                                name={
                                    requirement.isVisible
                                        ? 'VisibilityOutlinedIcon'
                                        : 'VisibilityOffOutlinedIcon'
                                }
                                size={20}
                            />
                        </FormIconButton>
                    </Tooltip>
                    <Tooltip
                        title={t('saveJob.removeRequirement')}
                        placement={TooltipPlacement.TOP_START}
                    >
                        <IconButton size="small" onClick={onDelete}>
                            <Icon name="TrashIcon" size={20} />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </StyledGrid>
        </div>
    );
};

export default RequirementItem;
